/**
 * Collection Video Manager - Custom Vimeo Controls
 * Handles play/pause, seek, mute, and fullscreen functionality
 */

class CollectionVideoManager {
  constructor() {
    this.init();
  }

  init() {
    // Initialize controls for all existing videos
    this.setupVideoControls();
    
    // Setup observer for dynamically loaded content (infinite scroll)
    this.setupMutationObserver();
  }

  setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node contains vimeo video containers
              const vimeoContainers = node.querySelectorAll ? 
                node.querySelectorAll('.vimeo-video-container') : [];
              
              if (node.classList && node.classList.contains('vimeo-video-container')) {
                this.setupSingleVideoControls(node);
              }
              
              vimeoContainers.forEach(container => {
                this.setupSingleVideoControls(container);
              });
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  setupVideoControls() {
    const vimeoContainers = document.querySelectorAll('.vimeo-video-container');
    vimeoContainers.forEach(container => {
      this.setupSingleVideoControls(container);
    });
  }

  setupSingleVideoControls(container) {
    const video = container.querySelector('.vimeo-direct-video');
    const controls = container.querySelector('.vimeo-custom-controls');
    
    if (!video || !controls || container.dataset.controlsSetup) return;
    
    // Mark as setup to prevent duplicate initialization
    container.dataset.controlsSetup = 'true';
    
    // Check video duration when metadata loads
    video.addEventListener('loadedmetadata', () => {
      console.log('Video duration detected:', video.duration, 'seconds');
      // If video duration is 3 seconds or less, hide controls completely
      if (video.duration && video.duration <= 3) {
        console.log('Short video detected - hiding controls');
        controls.classList.add('hidden-short-video');
        // Controls remain display:none by default CSS
        return; // Skip setting up control interactions
      }
      
      console.log('Long video detected - showing controls');
      // For videos longer than 3 seconds, show controls and setup interactions
      controls.classList.add('show-controls');
      this.setupControlInteractions(video, controls);
    });
    
    // Fallback: if metadata is already loaded, check duration immediately
    if (video.readyState >= 1) { // HAVE_METADATA or higher
      console.log('Fallback - Video duration already loaded:', video.duration, 'seconds');
      if (video.duration && video.duration <= 3) {
        console.log('Fallback - Short video detected - hiding controls');
        controls.classList.add('hidden-short-video');
        // Controls remain display:none by default CSS
        return;
      }
      console.log('Fallback - Long video detected - showing controls');
      controls.classList.add('show-controls');
      this.setupControlInteractions(video, controls);
    }
  }

  setupControlInteractions(video, controls) {
    const playBtn = controls.querySelector('.vimeo-play-btn');
    const playIcon = controls.querySelector('.play-icon');
    const pauseIcon = controls.querySelector('.pause-icon');
    const forwardBtn = controls.querySelector('.vimeo-forward-btn');
    const muteBtn = controls.querySelector('.vimeo-mute-btn');
    const equalizer = controls.querySelector('.vimeo-equalizer');
    const fullscreenBtn = controls.querySelector('.vimeo-fullscreen-btn');
    const progress = controls.querySelector('.vimeo-progress');
    const seek = controls.querySelector('.vimeo-seek');

    // Play/Pause functionality
    if (playBtn) {
      playBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (video.paused) {
          video.play();
          if (playIcon) playIcon.style.display = 'none';
          if (pauseIcon) pauseIcon.style.display = 'block';
        } else {
          video.pause();
          if (playIcon) playIcon.style.display = 'block';
          if (pauseIcon) pauseIcon.style.display = 'none';
        }
      });
    }

    // Forward functionality (2 seconds)
    if (forwardBtn) {
      forwardBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (video.duration) {
          const newTime = Math.min(video.currentTime + 2, video.duration);
          video.currentTime = newTime;
        }
      });
    }

    // Mute/Unmute functionality with opacity
    if (muteBtn && equalizer) {
      muteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (video.muted) {
          video.muted = false;
          equalizer.classList.add('unmuted');
        } else {
          video.muted = true;
          equalizer.classList.remove('unmuted');
        }
      });
    }

    // Fullscreen functionality
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.mozRequestFullScreen) {
          container.mozRequestFullScreen();
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen();
        }
      });
    }

    // Timeline/Seek functionality
    if (seek && progress) {
      // Update progress as video plays
      video.addEventListener('timeupdate', () => {
        if (video.duration) {
          const progressPercent = (video.currentTime / video.duration) * 100;
          progress.value = progressPercent;
          seek.value = progressPercent;
        }
      });

      // Seek when user interacts with timeline
      seek.addEventListener('input', (e) => {
        const seekTime = (e.target.value / 100) * video.duration;
        video.currentTime = seekTime;
      });

      // Prevent video control when interacting with custom controls
      seek.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Video state change handlers
    video.addEventListener('play', () => {
      if (playIcon && pauseIcon) {
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'block';
      }
    });

    video.addEventListener('pause', () => {
      if (playIcon && pauseIcon) {
        playIcon.style.display = 'block';
        pauseIcon.style.display = 'none';
      }
    });

    // Initialize control states based on video state
    if (video.paused) {
      if (playIcon) playIcon.style.display = 'block';
      if (pauseIcon) pauseIcon.style.display = 'none';
    } else {
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'block';
    }

    // Initialize equalizer opacity based on mute state
    if (equalizer) {
      if (video.muted) {
        equalizer.classList.remove('unmuted');
      } else {
        equalizer.classList.add('unmuted');
      }
    }

    // Prevent default video controls from interfering
    video.addEventListener('click', (e) => {
      // Only prevent default if custom controls are enabled
      if (controls) {
        e.preventDefault();
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionVideoManager();
  });
} else {
  new CollectionVideoManager();
}

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionVideoManager;
}